import React from 'react';
import {
  Map,
  Layers,
  Zap,
  Eye,
  Compass,
  Mountain,
  Target,
  Sparkles,
  BookOpen,
  Play,
  Navigation,
  Star
} from 'lucide-react';

export const VisualInspirationDirection: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Eye className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-4xl font-bold text-charcoal-900">Visual Inspiration & Direction</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
          Inspiration models that embody Peak Priorities' philosophical depth: revealing clarity, navigating context, scaling with structure, and sparking transformation.
        </p>
      </div>

      {/* Core Philosophy */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center gap-3 mb-4">
          <Star className="h-6 w-6 text-[var(--mustard)]" />
          <h2 className="text-2xl font-bold text-charcoal-900">Why Our Visual System Can't Just Be "Pretty"</h2>
        </div>
        <p className="text-charcoal-700 leading-relaxed mb-6">
          Peak Priorities isn't a brand that says "trust us"—we're a brand that shows the path. Our visual identity must embody philosophical depth and create genuine transformation.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border border-consulting-slate-blue-200">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Must Reveal Clarity</h3>
            <p className="text-charcoal-700 text-sm">
              Not just declare it. Visuals should make invisible systems visible, showing how complexity becomes manageable through proper navigation.
            </p>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-sage-50 to-amber-50 border border-sage-200">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Must Navigate Context</h3>
            <p className="text-charcoal-700 text-sm">
              Not just show terrain. Every visual should provide wayfinding, helping people understand where they are and where they're going.
            </p>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border border-[var(--mustard)]">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Must Scale with Structure</h3>
            <p className="text-charcoal-700 text-sm">
              Not flatten systems. Show how individual work connects to team dynamics and organizational transformation.
            </p>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border border-consulting-slate-blue-200">
            <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Must Spark Transformation</h3>
            <p className="text-charcoal-700 text-sm">
              Not illustrate status. Create visual moments that show catalytic change, energy conversion, and movement from chaos to clarity.
            </p>
          </div>
        </div>
      </div>

      {/* Inspiration Categories */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center gap-3 mb-6">
          <Map className="h-6 w-6 text-sage-600" />
          <h2 className="text-2xl font-bold text-charcoal-900">Inspiration Models Worth Emulating</h2>
        </div>
        
        <div className="space-y-8">
          {/* Cartographic Design */}
          <div className="border-l-4 border-consulting-slate-blue-600 pl-6">
            <div className="flex items-center gap-3 mb-3">
              <Compass className="h-6 w-6 text-consulting-slate-blue-600" />
              <h3 className="text-xl font-semibold text-charcoal-900">Cartographic Design</h3>
            </div>
            <p className="text-charcoal-700 mb-4">
              <strong>Inspiration Sources:</strong> National Park Maps, Patagonia Trails, Topographic Surveys
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Relevance to Peak Priorities</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>Expedition and navigation aesthetic</li>
                  <li>Elevation and perspective visualization</li>
                  <li>Wayfinding and route planning</li>
                  <li>Terrain complexity made navigable</li>
                </ul>
              </div>
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Key Visual Elements</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>Contour lines and elevation gradients</li>
                  <li>Trail markers and waypoints</li>
                  <li>Legend systems and scale indicators</li>
                  <li>Layered information architecture</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Fractal / Nested Design */}
          <div className="border-l-4 border-sage-600 pl-6">
            <div className="flex items-center gap-3 mb-3">
              <Layers className="h-6 w-6 text-sage-600" />
              <h3 className="text-xl font-semibold text-charcoal-900">Fractal / Nested Design</h3>
            </div>
            <p className="text-charcoal-700 mb-4">
              <strong>Inspiration Sources:</strong> Figma's organizational structure map, Mandelbrot sets, Russian nesting dolls
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Relevance to Peak Priorities</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>Multi-level clarity from individual to org</li>
                  <li>Self-similar patterns at different scales</li>
                  <li>Systemic layers of impact visualization</li>
                  <li>Zoomable complexity management</li>
                </ul>
              </div>
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Key Visual Elements</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>Recursive geometric patterns</li>
                  <li>Scale-adaptive information density</li>
                  <li>Nested container relationships</li>
                  <li>Fractal-based spacing systems</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Interactive Systems */}
          <div className="border-l-4 border-[var(--mustard)] pl-6">
            <div className="flex items-center gap-3 mb-3">
              <Zap className="h-6 w-6 text-[var(--mustard)]" />
              <h3 className="text-xl font-semibold text-charcoal-900">Interactive Systems</h3>
            </div>
            <p className="text-charcoal-700 mb-4">
              <strong>Inspiration Sources:</strong> Notion, Obsidian, Roam graphs, Miro boards
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Relevance to Peak Priorities</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>Visualize knowledge and alignment</li>
                  <li>Contextual shifts and connections</li>
                  <li>Dynamic relationship mapping</li>
                  <li>Real-time collaborative clarity</li>
                </ul>
              </div>
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Key Visual Elements</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>Node-based connection systems</li>
                  <li>Interactive filtering and layering</li>
                  <li>Contextual information overlays</li>
                  <li>Gesture-based navigation patterns</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Game-like UI */}
          <div className="border-l-4 border-consulting-slate-blue-600 pl-6">
            <div className="flex items-center gap-3 mb-3">
              <Play className="h-6 w-6 text-consulting-slate-blue-600" />
              <h3 className="text-xl font-semibold text-charcoal-900">Game-like UI</h3>
            </div>
            <p className="text-charcoal-700 mb-4">
              <strong>Inspiration Sources:</strong> Fortnite maps, Disney theme park design, Pokémon GO
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Relevance to Peak Priorities</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>Immersive and playful exploration</li>
                  <li>Showing depth and discovery</li>
                  <li>Progress visualization and achievement</li>
                  <li>Engaging complexity management</li>
                </ul>
              </div>
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Key Visual Elements</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>3D perspective and depth cues</li>
                  <li>Progress bars and achievement systems</li>
                  <li>Interactive hotspots and discoveries</li>
                  <li>Reward and feedback animations</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Metaphorical Frameworks */}
          <div className="border-l-4 border-sage-600 pl-6">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-sage-600" />
              <h3 className="text-xl font-semibold text-charcoal-900">Metaphorical Frameworks</h3>
            </div>
            <p className="text-charcoal-700 mb-4">
              <strong>Inspiration Sources:</strong> Wardley Maps, Lumen Trails, Oura Rings, Apple Health
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Relevance to Peak Priorities</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>Strategic visualization and insight</li>
                  <li>Layers of input and contextual data</li>
                  <li>Personal and organizational metrics</li>
                  <li>Trend analysis and pattern recognition</li>
                </ul>
              </div>
              <div className="p-4 bg-[var(--surface)]">
                <h4 className="font-semibold text-charcoal-900 mb-2">Key Visual Elements</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  <li>Data visualization with narrative flow</li>
                  <li>Multi-dimensional metric displays</li>
                  <li>Trend lines and pattern overlays</li>
                  <li>Contextual insight tooltips</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Visual Direction Principles */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center gap-3 mb-4">
          <Navigation className="h-6 w-6 text-[var(--mustard)]" />
          <h2 className="text-2xl font-bold text-charcoal-900">Visual Direction Principles</h2>
        </div>
        <p className="text-charcoal-700 leading-relaxed mb-6">
          How to translate these inspiration sources into Peak Priorities' unique visual language.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Design Approach</h3>
            <ul className="list-disc list-inside text-charcoal-700 space-y-2">
              <li><strong>Layered Complexity:</strong> Show multiple levels of information simultaneously</li>
              <li><strong>Progressive Disclosure:</strong> Reveal detail as users zoom in or explore</li>
              <li><strong>Contextual Navigation:</strong> Always show where you are and where you can go</li>
              <li><strong>Transformational Moments:</strong> Highlight catalytic change and energy conversion</li>
              <li><strong>Systemic Thinking:</strong> Connect individual actions to organizational outcomes</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-xl font-semibold text-charcoal-900 mb-3">Implementation Strategy</h3>
            <ul className="list-disc list-inside text-charcoal-700 space-y-2">
              <li><strong>Start with Maps:</strong> Use cartographic principles for all navigation</li>
              <li><strong>Add Fractals:</strong> Show self-similar patterns at different scales</li>
              <li><strong>Enable Interaction:</strong> Make connections and relationships explorable</li>
              <li><strong>Gamify Progress:</strong> Show achievement and transformation visually</li>
              <li><strong>Measure Everything:</strong> Use data visualization to reveal insights</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Mood Board Section */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="h-6 w-6 text-consulting-slate-blue-600" />
          <h2 className="text-2xl font-bold text-charcoal-900">Visual Mood Board</h2>
        </div>
        <p className="text-charcoal-700 leading-relaxed mb-6">
          Key visual elements and styles that embody Peak Priorities' unique approach to clarity and transformation.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-6 bg-gradient-to-br from-consulting-slate-blue-50 to-sage-50 border border-consulting-slate-blue-200">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Color Palette</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-consulting-slate-blue-600 rounded"></div>
                <span className="text-sm text-charcoal-700">Primary Blue - Steady navigation</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-sage-600 rounded"></div>
                <span className="text-sm text-charcoal-700">Sage Green - Growth & progress</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[var(--mustard)] rounded"></div>
                <span className="text-sm text-charcoal-700">Amber - Energy & transformation</span>
              </div>
            </div>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-sage-50 to-amber-50 border border-sage-200">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Typography</h3>
            <div className="space-y-2">
              <div className="text-lg font-bold text-charcoal-900">Inter Bold</div>
              <div className="text-base text-charcoal-700">Inter Regular</div>
              <div className="text-sm text-charcoal-600">Inter Light</div>
              <p className="text-xs text-charcoal-500 mt-2">Clean, readable, professional hierarchy</p>
            </div>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-amber-50 to-consulting-slate-blue-50 border border-[var(--mustard)]">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-3">Visual Elements</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Compass className="h-4 w-4 text-[var(--mustard)]" />
                <span className="text-sm text-charcoal-700">Wayfinding icons</span>
              </div>
              <div className="flex items-center gap-2">
                <Mountain className="h-4 w-4 text-sage-600" />
                <span className="text-sm text-charcoal-700">Elevation metaphors</span>
              </div>
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-consulting-slate-blue-600" />
                <span className="text-sm text-charcoal-700">Nested systems</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-8 text-center">
        <h3 className="text-2xl font-bold text-charcoal-900 mb-3">
          From Inspiration to Implementation
        </h3>
        <p className="text-charcoal-700 mb-6 max-w-2xl mx-auto">
          Use these inspiration models and principles to create visual experiences that truly embody Peak Priorities' unique approach to clarity, navigation, and transformation.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href="/elevation/best-practices-design-principles" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium text-white bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700">
            <Star className="h-5 w-5 mr-2" />
            View Design Principles
          </a>
          <a href="/elevation/logo-system" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium text-white bg-sage-600 hover:bg-sage-700">
            <Compass className="h-5 w-5 mr-2" />
            Explore Logo System
          </a>
          <a href="/elevation/lab/brand-guidelines-document" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium text-white bg-[var(--mustard)] hover:bg-[var(--mustard)]">
            <BookOpen className="h-5 w-5 mr-2" />
            Read Guidelines
          </a>
        </div>
      </div>
    </div>
  );
};
